import type { EditorView } from "prosemirror-view";

export const EDITOR_V3_DROP_CURSOR_CLASS = "editor-v3-drop-cursor";
export const EDITOR_V3_DROP_CURSOR_COLOR = "var(--primary)";
export const EDITOR_V3_DROP_CURSOR_WIDTH = 3;

type DropCursorRect = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

const getEditorScale = (view: EditorView) => {
  const editorRect = view.dom.getBoundingClientRect();

  return {
    scaleX: view.dom.offsetWidth ? editorRect.width / view.dom.offsetWidth : 1,
    scaleY: view.dom.offsetHeight
      ? editorRect.height / view.dom.offsetHeight
      : 1,
  };
};

const getParentOffset = (parent: HTMLElement | null) => {
  if (
    !parent ||
    (parent === document.body && getComputedStyle(parent).position === "static")
  ) {
    return {
      left: -pageXOffset,
      top: -pageYOffset,
    };
  }

  const parentRect = parent.getBoundingClientRect();
  const parentScaleX = parent.offsetWidth
    ? parentRect.width / parent.offsetWidth
    : 1;
  const parentScaleY = parent.offsetHeight
    ? parentRect.height / parent.offsetHeight
    : 1;

  return {
    left: parentRect.left - parent.scrollLeft * parentScaleX,
    top: parentRect.top - parent.scrollTop * parentScaleY,
  };
};

const getBlockDropCursorRect = ({
  position,
  scaleY,
  view,
  width,
}: {
  position: number;
  scaleY: number;
  view: EditorView;
  width: number;
}) => {
  const $position = view.state.doc.resolve(position);
  const before = $position.nodeBefore;
  const after = $position.nodeAfter;

  if (!before && !after) {
    return null;
  }

  const nodeDOM = view.nodeDOM(position - (before ? before.nodeSize : 0));

  if (!(nodeDOM instanceof Element)) {
    return null;
  }

  const nodeRect = nodeDOM.getBoundingClientRect();
  let top = before ? nodeRect.bottom : nodeRect.top;

  if (before && after) {
    const afterDOM = view.nodeDOM(position);

    if (afterDOM instanceof Element) {
      top = (top + afterDOM.getBoundingClientRect().top) / 2;
    }
  }

  const halfWidth = (width / 2) * scaleY;

  return {
    bottom: top + halfWidth,
    left: nodeRect.left,
    right: nodeRect.right,
    top: top - halfWidth,
  };
};

const getInlineDropCursorRect = ({
  position,
  scaleX,
  view,
  width,
}: {
  position: number;
  scaleX: number;
  view: EditorView;
  width: number;
}) => {
  const coords = view.coordsAtPos(position);
  const halfWidth = (width / 2) * scaleX;

  return {
    bottom: coords.bottom,
    left: coords.left - halfWidth,
    right: coords.left + halfWidth,
    top: coords.top,
  };
};

export const updateEditorDropCursorElement = ({
  cursor,
  position,
  view,
  width = EDITOR_V3_DROP_CURSOR_WIDTH,
}: {
  cursor: HTMLElement;
  position: number;
  view: EditorView;
  width?: number;
}) => {
  const $position = view.state.doc.resolve(position);
  const isBlock = !$position.parent.inlineContent;
  const { scaleX, scaleY } = getEditorScale(view);
  let rect: DropCursorRect | null = null;

  if (isBlock) {
    rect = getBlockDropCursorRect({
      position,
      scaleY,
      view,
      width,
    });
  }

  rect ??= getInlineDropCursorRect({
    position,
    scaleX,
    view,
    width,
  });

  const parent =
    cursor.parentElement instanceof HTMLElement ? cursor.parentElement : null;
  const parentOffset = getParentOffset(parent);

  cursor.hidden = false;
  cursor.style.backgroundColor = EDITOR_V3_DROP_CURSOR_COLOR;
  cursor.classList.toggle("prosemirror-dropcursor-block", isBlock);
  cursor.classList.toggle("prosemirror-dropcursor-inline", !isBlock);
  cursor.style.left = `${(rect.left - parentOffset.left) / scaleX}px`;
  cursor.style.top = `${(rect.top - parentOffset.top) / scaleY}px`;
  cursor.style.width = `${(rect.right - rect.left) / scaleX}px`;
  cursor.style.height = `${(rect.bottom - rect.top) / scaleY}px`;
};
